export default {
  "slug": "voice-evacuation-intelligibility",
  "title": "Voice Evacuation Intelligibility, Explained (NFPA 72 Chapter 24)",
  "description": "Why voice evacuation messages must be intelligible, how acoustically distinguishable spaces (ADS) and speech-intelligibility metrics work under NFPA 72 Chapter 24, and what makes announcements clear under alarm conditions.",
  "keywords": "fire alarm voice evacuation intelligibility, NFPA 72 chapter 24, acoustically distinguishable space, STI CIS, emergency communication system, mass notification",
  "faqs": [
    {
      "q": "What does 'intelligible' actually mean for voice evacuation?",
      "a": "Intelligible means occupants can understand the words of the message, not just hear that a sound is playing. NFPA 72 Chapter 24 addresses this through acoustically distinguishable spaces and speech-intelligibility measurement, so a message like 'evacuate via the north stair' is actually understood."
    },
    {
      "q": "What is an acoustically distinguishable space (ADS)?",
      "a": "An ADS is an area with acoustic characteristics distinct enough that it's evaluated separately for intelligibility. The designer identifies which ADSs require intelligibility, which only require audibility, and which are excluded — because a mechanical room and a carpeted office behave very differently acoustically."
    },
    {
      "q": "How is intelligibility measured?",
      "a": "Commonly with a speech-intelligibility metric such as STI or CIS. NFPA 72 Annex material references a target range (frequently cited as a Common Intelligibility Scale value of about 0.45 or higher) measured in required ADSs. Reverberation, background noise, and speaker placement all affect the result."
    }
  ],
  "category": "Emergency Communications",
  "readMins": 6,
  "published": "2026-09-27",
  "updated": "2026-09-27",
  "hero": "https://images.unsplash.com/photo-1787429161273-8143848ddf59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjZWlsaW5nJTIwc3BlYWtlciUyMHB1YmxpYyUyMGFkZHJlc3N8ZW58MHx8fHwxNzkwNTI2MDE0fDA&ixlib=rb-4.1.0&q=85",
  "heroAlt": "Horn and speaker array used for voice evacuation and mass notification",
  "excerpt": "A voice message no one can understand is worse than a tone. Here's how Chapter 24 turns 'clear enough to act on' into something you can design and measure.",
  "sections": [
    {
      "h": "Audible is not the same as intelligible",
      "p": "A tone only needs to be loud enough to notice. A voice message must be loud enough AND clear enough to understand the instruction. NFPA 72 Chapter 24 governs emergency communication systems and introduces intelligibility as a design and acceptance requirement in the spaces that need it."
    },
    {
      "h": "Start by mapping acoustically distinguishable spaces",
      "p": "The designer divides the building into acoustically distinguishable spaces (ADS). For each, they decide whether intelligibility is required, only audibility is required, or the space is excluded. This matters because the same speaker layout that's clear in an office would be unintelligible in a reverberant atrium — so each ADS is judged on its own acoustics."
    },
    {
      "h": "What degrades intelligibility",
      "p": "Three big factors: reverberation (hard, reflective surfaces smear speech), background noise (HVAC, crowds, machinery), and speaker coverage (too few, too far, or poorly aimed). High ceilings and glass or masonry surfaces are common problem environments where more, closer, or better-directed speakers are needed."
    },
    {
      "h": "Measuring and verifying",
      "p": "Intelligibility is quantified with metrics like STI or CIS. Chapter 24's annex guidance points to a target range (a CIS around 0.45 or higher is frequently referenced) measured in the required ADSs during acceptance. Predictive modeling during design plus field measurement at acceptance is the reliable path to a passing result."
    },
    {
      "h": "Design habits that help",
      "p": "Favor more speakers at lower volume over a few loud ones, aim speakers to reduce reflections, coordinate with the architecture on acoustic treatment where feasible, and keep message scripts short and simple. Always verify final performance against the adopted edition and the AHJ's acceptance criteria."
    }
  ],
  "relatedModules": [
    "emergency-comm",
    "design-principles"
  ],
  "relatedGuides": [
    "notification-appliance-strobe-spacing"
  ]
};
